import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { BranchCode, UserRole } from '@lorsamthai/ops-shared';
import { api, clearToken, getToken } from './api.js';

export interface Me {
  id: string;
  line_user_id: string;
  name: string;
  role: UserRole;
  branch: BranchCode | null;
}

interface AuthState {
  status: 'loading' | 'authed' | 'anon';
  me: Me | null;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = async () => {
    if (!getToken()) {
      setStatus('anon');
      setMe(null);
      return;
    }
    try {
      const data = await api<Me>('/auth/me');
      setMe(data);
      setStatus('authed');
    } catch {
      clearToken();
      setMe(null);
      setStatus('anon');
    }
  };

  useEffect(() => {
    void loadMe();
  }, []);

  const logout = () => {
    clearToken();
    setMe(null);
    setStatus('anon');
  };

  return (
    <AuthContext.Provider value={{ status, me, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
